import { useCallback, useEffect, useState } from 'react'
import { FileCode2, Loader2 } from 'lucide-react'
import { snippetApi } from '@/api/services'
import { cn } from '@/lib/utils'
import type { Snippet, SnippetType } from '@/types'

const SNIPPET_DRAG_TYPE = 'text/x-snippet'

type Props = {
  type: SnippetType
  onInsert: (content: string) => void
  className?: string
  compact?: boolean
}

export function SnippetPicker({ type, onInsert, className, compact = false }: Props) {
  const [snippets, setSnippets] = useState<Snippet[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await snippetApi.getAll({ type, pageNumber: 1, pageSize: 50 })
      setSnippets(res.items)
    } catch {
      setSnippets([])
    } finally {
      setLoading(false)
    }
  }, [type])

  useEffect(() => { load() }, [load])

  if (loading) {
    return (
      <div className={cn('flex items-center gap-2 text-[10px] text-muted', className)}>
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading snippets…
      </div>
    )
  }

  if (snippets.length === 0) {
    return (
      <p className={cn('text-[10px] text-muted font-mono', className)}>
        No snippets for this type yet.
      </p>
    )
  }

  return (
    <div className={cn('space-y-1', className)}>
      {!compact && (
        <p className="text-[10px] font-bold tracking-widest uppercase text-muted">Snippets</p>
      )}
      <div className={cn('space-y-1', compact ? 'max-h-32 overflow-y-auto' : 'max-h-48 overflow-y-auto')}>
        {snippets.map(s => (
          <button
            key={s.id}
            type="button"
            draggable
            onDragStart={e => {
              e.dataTransfer.setData(SNIPPET_DRAG_TYPE, s.content)
              e.dataTransfer.setData('text/plain', s.content)
              e.dataTransfer.effectAllowed = 'copy'
            }}
            onClick={() => onInsert(s.content)}
            className="flex w-full items-start gap-2 rounded-md border border-border px-2.5 py-2 text-left hover:border-border2 hover:bg-bg/60 transition-colors cursor-grab active:cursor-grabbing"
            title={s.description || s.name}
          >
            <FileCode2 className="h-3 w-3 text-accent flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <span className="block text-[11px] font-mono text-ink truncate">{s.name}</span>
              {s.description && !compact && (
                <span className="block text-[10px] text-muted truncate mt-0.5">{s.description}</span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

export { SNIPPET_DRAG_TYPE }
